import { useRef, useEffect, useState } from 'react';
import { Flame, Trophy, Target, Star, Calendar, Award } from 'lucide-react';

import { Card, CardContent } from '../ui/card';

import { CDN_URL } from '@/config/cdn';

// ============================================
// Sound Effects (CloudFront-hosted audio files)
// ============================================
const SOUND_URLS = {
  success: `${CDN_URL}/audio/sfx/success-chime.mp3`,
  streakBest: `${CDN_URL}/audio/sfx/streak-best.mp3`,
  gong: `${CDN_URL}/audio/sfx/gong.mp3`
};

// Default mastery config (fallback)
const DEFAULT_MASTERY_CONFIG = {
  mastery_levels: [
    { level: 'shokyu', threshold: 2, bonus_points: 5 },
    { level: 'chukyu', threshold: 4, bonus_points: 10 },
    { level: 'jokyu', threshold: 8, bonus_points: 25 },
    { level: 'tatsujin', threshold: 16, bonus_points: 50 },
  ],
  mastered_threshold: 8
};

// Japanese mastery level display names
const MASTERY_LEVEL_DISPLAY = {
  shokyu: { label: 'Shokyu', japanese: '初級', color: 'bg-green-400', textColor: 'text-green-600' },
  chukyu: { label: 'Chukyu', japanese: '中級', color: 'bg-blue-400', textColor: 'text-blue-600' },
  jokyu: { label: 'Jokyu', japanese: '上級', color: 'bg-purple-500', textColor: 'text-purple-600' },
  tatsujin: { label: 'Tatsujin', japanese: '達人', color: 'bg-yellow-500', textColor: 'text-yellow-600' },
};

// Play success chime sound
const playSuccessSound = () => {
  try {
    const audio = new Audio(SOUND_URLS.success);
    audio.volume = 0.3;
    audio.play().catch(() => {}); // Ignore autoplay restrictions
  } catch (e) {
    // Ignore audio errors
  }
};

// Play fire whoosh sound for new streak best
const playStreakBestSound = () => {
  try {
    const audio = new Audio(SOUND_URLS.streakBest);
    audio.volume = 0.5;
    audio.play().catch(() => {}); // Ignore autoplay restrictions
  } catch (e) {
    // Ignore audio errors
  }
};

// Play gong sound for word mastery
const playGongSound = () => {
  try {
    const audio = new Audio(SOUND_URLS.gong);
    audio.volume = 0.5;
    audio.play().catch(() => {}); // Ignore autoplay restrictions
  } catch (e) {
    // Ignore audio errors
  }
};

// Calculate mastery level distribution from term_mastery data using config thresholds
const calculateMasteryDistribution = (termMastery = {}, masteryConfig = DEFAULT_MASTERY_CONFIG) => {
  const levels = masteryConfig.mastery_levels;
  const distribution = {
    unlearned: 0,  // 0 correct
    shokyu: 0,     // Beginner
    chukyu: 0,     // Intermediate
    jokyu: 0,      // Advanced (mastered)
    tatsujin: 0    // Master
  };

  // Get thresholds from config
  const thresholds = {};
  levels.forEach(l => {
    thresholds[l.level] = l.threshold;
  });

  Object.values(termMastery).forEach(count => {
    if (count === 0) {
      distribution.unlearned++;
    } else if (count >= (thresholds.tatsujin || 16)) {
      distribution.tatsujin++;
    } else if (count >= (thresholds.jokyu || 8)) {
      distribution.jokyu++;
    } else if (count >= (thresholds.chukyu || 4)) {
      distribution.chukyu++;
    } else if (count >= (thresholds.shokyu || 2)) {
      distribution.shokyu++;
    } else {
      distribution.unlearned++;
    }
  });

  return distribution;
};

/**
 * Displays vocabulary quiz progress stats including streaks, daily counts, and mastery.
 */
const VocabProgressStats = ({
  currentCorrectStreak = 0,
  longestCorrectStreak = 0,
  todayCorrectCount = 0,
  bestDailyCorrectCount = 0,
  totalMastered = 0,
  totalAttempted = 0,
  categoryLevels = {},
  termMastery = {},
  masteryConfig = DEFAULT_MASTERY_CONFIG,
  compact = false
}) => {
  const masteryDist = calculateMasteryDistribution(termMastery, masteryConfig);
  // Count mastered as jokyu + tatsujin (advanced levels)
  const masteredCount = masteryDist.jokyu + masteryDist.tatsujin;
  // Track previous values to detect increases
  const prevValues = useRef({
    streak: currentCorrectStreak,
    longestStreak: longestCorrectStreak,
    todayCount: todayCorrectCount,
    mastered: totalMastered,
    initialized: false
  });

  // Animation states
  const [animatingStreak, setAnimatingStreak] = useState(false);
  const [animatingStreakBest, setAnimatingStreakBest] = useState(false);
  const [animatingToday, setAnimatingToday] = useState(false);
  const [animatingMastered, setAnimatingMastered] = useState(false);

  // Detect changes and trigger animations/sounds
  // Sequence: success chime → streak best fire whoosh → gong
  useEffect(() => {
    const prev = prevValues.current;

    // Skip initial render to avoid playing sounds on page load
    if (!prev.initialized) {
      prevValues.current = {
        streak: currentCorrectStreak,
        longestStreak: longestCorrectStreak,
        todayCount: todayCorrectCount,
        mastered: totalMastered,
        initialized: true
      };
      return;
    }

    const streakIncreased = currentCorrectStreak > prev.streak;
    const newStreakBest = longestCorrectStreak > prev.longestStreak;
    const todayIncreased = todayCorrectCount > prev.todayCount;
    const masteryIncreased = totalMastered > prev.mastered;

    let currentDelay = 0;

    // Step 1: Success chime for streak/today increase
    if (streakIncreased || todayIncreased) {
      setAnimatingStreak(streakIncreased);
      setAnimatingToday(todayIncreased);
      playSuccessSound();
      setTimeout(() => {
        setAnimatingStreak(false);
        setAnimatingToday(false);
      }, 400);
      currentDelay = 500; // Wait for success chime
    }

    // Step 2: Fire whoosh for new streak best
    if (newStreakBest) {
      setTimeout(() => {
        setAnimatingStreakBest(true);
        playStreakBestSound();
        setTimeout(() => setAnimatingStreakBest(false), 600);
      }, currentDelay);
      currentDelay += 600; // Wait for fire whoosh
    }

    // Step 3: Gong for mastery
    if (masteryIncreased) {
      setTimeout(() => {
        setAnimatingMastered(true);
        playGongSound();
        setTimeout(() => setAnimatingMastered(false), 600);
      }, currentDelay);
    }

    // Update previous values
    prevValues.current = {
      streak: currentCorrectStreak,
      longestStreak: longestCorrectStreak,
      todayCount: todayCorrectCount,
      mastered: totalMastered,
      initialized: true
    };
  }, [currentCorrectStreak, longestCorrectStreak, todayCorrectCount, totalMastered]);

  // Animation class helper
  const getAnimClass = (isAnimating, type = 'pulse') => {
    if (!isAnimating) return '';
    if (type === 'gong') return 'animate-gong';
    if (type === 'fire') return 'animate-fire';
    return 'animate-stat-pulse';
  };
  if (compact) {
    // Compact inline display for quiz header
    return (
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <div className={`flex items-center gap-1.5 bg-orange-50 dark:bg-orange-900/30 px-3 py-1.5 rounded-full transition-transform ${getAnimClass(animatingStreak)}`}>
          <Flame className={`h-4 w-4 ${currentCorrectStreak > 0 ? 'text-orange-500' : 'text-gray-300 dark:text-gray-600'}`} />
          <span className="font-medium text-orange-700 dark:text-orange-400">{currentCorrectStreak}</span>
          <span className="text-orange-600 dark:text-orange-500 text-xs">streak</span>
        </div>

        <div className={`flex items-center gap-1.5 bg-green-50 dark:bg-green-900/30 px-3 py-1.5 rounded-full transition-transform ${getAnimClass(animatingToday)}`}>
          <Target className="h-4 w-4 text-green-500" />
          <span className="font-medium text-green-700 dark:text-green-400">{todayCorrectCount}</span>
          <span className="text-green-600 dark:text-green-500 text-xs">today</span>
        </div>

        {/* Mastery breakdown pill */}
        <div className={`flex items-center gap-1 bg-purple-50 dark:bg-purple-900/30 px-3 py-1.5 rounded-full transition-transform ${getAnimClass(animatingMastered, 'gong')}`}>
          <Star className="h-4 w-4 text-purple-500" />
          <div className="flex items-center gap-0.5 text-xs">
            {masteryDist.shokyu > 0 && (
              <span className="font-medium text-green-600" title="Shokyu (初級)">{masteryDist.shokyu}</span>
            )}
            {masteryDist.chukyu > 0 && (
              <>
                {masteryDist.shokyu > 0 && <span className="text-gray-300">/</span>}
                <span className="font-medium text-blue-600" title="Chukyu (中級)">{masteryDist.chukyu}</span>
              </>
            )}
            {masteryDist.jokyu > 0 && (
              <>
                {(masteryDist.shokyu > 0 || masteryDist.chukyu > 0) && <span className="text-gray-300">/</span>}
                <span className="font-medium text-purple-600" title="Jokyu (上級)">{masteryDist.jokyu}</span>
              </>
            )}
            {masteryDist.tatsujin > 0 && (
              <>
                {(masteryDist.shokyu > 0 || masteryDist.chukyu > 0 || masteryDist.jokyu > 0) && <span className="text-gray-300">/</span>}
                <span className="font-bold text-yellow-600" title="Tatsujin (達人)">{masteryDist.tatsujin}</span>
              </>
            )}
            {masteryDist.shokyu === 0 && masteryDist.chukyu === 0 && masteryDist.jokyu === 0 && masteryDist.tatsujin === 0 && (
              <span className="text-gray-400">0</span>
            )}
          </div>
          <span className="text-purple-600 text-xs">mastered</span>
        </div>
      </div>
    );
  }

  // Full card display
  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Trophy className="h-5 w-5 text-yellow-500" />
          Your Progress
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Current Streak */}
          <div className={`text-center p-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg transition-transform ${getAnimClass(animatingStreak)}`}>
            <Flame className={`h-6 w-6 mx-auto mb-1 ${currentCorrectStreak > 0 ? 'text-orange-500' : 'text-gray-300 dark:text-gray-600'}`} />
            <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">{currentCorrectStreak}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Correct Streak</p>
            <p className={`text-xs text-orange-400 ${getAnimClass(animatingStreakBest, 'fire')}`}>
              Best: {longestCorrectStreak}
            </p>
          </div>

          {/* Today's Correct */}
          <div className={`text-center p-3 bg-green-50 dark:bg-green-900/30 rounded-lg transition-transform ${getAnimClass(animatingToday)}`}>
            <Calendar className="h-6 w-6 mx-auto mb-1 text-green-500" />
            <p className="text-2xl font-bold text-green-600 dark:text-green-400">{todayCorrectCount}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Today's Correct</p>
            <p className="text-xs text-green-400">Best: {bestDailyCorrectCount}</p>
          </div>

          {/* Words Mastery Breakdown */}
          <div className={`text-center p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg transition-transform ${getAnimClass(animatingMastered, 'gong')}`}>
            <Star className="h-6 w-6 mx-auto mb-1 text-purple-500" />
            <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{masteredCount}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Words Mastered</p>
            {/* Mastery level breakdown bar - 4 Japanese levels */}
            <div className="flex items-center gap-0.5 mt-2 justify-center">
              {masteryDist.shokyu > 0 && (
                <div
                  className="h-1.5 bg-green-400 rounded-full"
                  style={{ width: `${Math.max(8, masteryDist.shokyu * 4)}px` }}
                  title={`${masteryDist.shokyu} Shokyu (初級)`}
                />
              )}
              {masteryDist.chukyu > 0 && (
                <div
                  className="h-1.5 bg-blue-400 rounded-full"
                  style={{ width: `${Math.max(8, masteryDist.chukyu * 4)}px` }}
                  title={`${masteryDist.chukyu} Chukyu (中級)`}
                />
              )}
              {masteryDist.jokyu > 0 && (
                <div
                  className="h-1.5 bg-purple-500 rounded-full"
                  style={{ width: `${Math.max(8, masteryDist.jokyu * 4)}px` }}
                  title={`${masteryDist.jokyu} Jokyu (上級)`}
                />
              )}
              {masteryDist.tatsujin > 0 && (
                <div
                  className="h-1.5 bg-yellow-500 rounded-full"
                  style={{ width: `${Math.max(8, masteryDist.tatsujin * 4)}px` }}
                  title={`${masteryDist.tatsujin} Tatsujin (達人)`}
                />
              )}
            </div>
            <p className="text-xs mt-1 flex justify-center gap-1">
              {masteryDist.shokyu > 0 && <span className="text-green-600">{masteryDist.shokyu}</span>}
              {masteryDist.shokyu > 0 && masteryDist.chukyu > 0 && <span className="text-gray-300">/</span>}
              {masteryDist.chukyu > 0 && <span className="text-blue-600">{masteryDist.chukyu}</span>}
              {(masteryDist.shokyu > 0 || masteryDist.chukyu > 0) && masteryDist.jokyu > 0 && <span className="text-gray-300">/</span>}
              {masteryDist.jokyu > 0 && <span className="text-purple-600">{masteryDist.jokyu}</span>}
              {(masteryDist.shokyu > 0 || masteryDist.chukyu > 0 || masteryDist.jokyu > 0) && masteryDist.tatsujin > 0 && <span className="text-gray-300">/</span>}
              {masteryDist.tatsujin > 0 && <span className="text-yellow-600 font-medium">{masteryDist.tatsujin}</span>}
            </p>
          </div>

          {/* Categories Unlocked */}
          <div className="text-center p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
            <Award className="h-6 w-6 mx-auto mb-1 text-blue-500" />
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {Object.values(categoryLevels).filter(l => l > 1).length}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Categories Advanced</p>
            <p className="text-xs text-blue-400">
              Avg Level: {Object.keys(categoryLevels).length > 0
                ? (Object.values(categoryLevels).reduce((a, b) => a + b, 0) / Object.keys(categoryLevels).length).toFixed(1)
                : '1.0'}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default VocabProgressStats;
